import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { ExternalLinkIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  cancelBillingSubscription,
  createManageSubscriptionSession,
  getBillingAttention,
  getSession,
  prepareBillingInvoice,
} from "./api";
import { BillingAttentionItem, User } from "./types";
import { AppContext } from "./context";
import { isAllAccessType } from "./allAccess";
import { PRICING, TIER_INFO } from "./pricingData";

const productName = (item: BillingAttentionItem) =>
  isAllAccessType(item.type)
    ? "ALL-ACCESS"
    : PRICING[item.type]?.displayName || item.type.toUpperCase();
const planName = (item: BillingAttentionItem) =>
  isAllAccessType(item.type)
    ? "Subscription"
    : item.type.startsWith("stories_")
    ? "Subscription"
    : TIER_INFO[item.tier].name;
const accessName = (item: BillingAttentionItem, user: User) => {
  const allAccess =
    !item.type.startsWith("stories_") &&
    user.subscriptions.some(
      (s) => isAllAccessType(s.type) && s.tier === "paid+"
    );
  if (allAccess) return "ALL-ACCESS";
  return TIER_INFO[
    user.subscriptions.find((s) => s.type === item.type)?.tier || "free"
  ].name;
};
const money = (amount: number, currency: string) => {
  const format = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  return format.format(
    amount / Math.pow(10, format.resolvedOptions().maximumFractionDigits)
  );
};
const statusLabel = (item: BillingAttentionItem) =>
  item.status === "paused"
    ? "Paused"
    : item.pendingUpdate
    ? "Upgrade pending"
    : ["past_due", "unpaid", "incomplete"].includes(item.status)
    ? "Payment required"
    : "Payment due";
const endDate = (item: BillingAttentionItem) =>
  item.currentPeriodEnd
    ? new Date(item.currentPeriodEnd).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "the end of the billing period";

export const BillingAttention = ({
  onRefresh,
  onTypesChange,
}: {
  onRefresh: () => Promise<void>;
  onTypesChange?: (types: BillingAttentionItem["type"][]) => void;
}) => {
  const { user, setUser } = React.useContext(AppContext);
  const [items, setItems] = React.useState<BillingAttentionItem[] | null>(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [selected, setSelected] = React.useState<BillingAttentionItem | null>(
    null
  );
  const [canceling, setCanceling] = React.useState(false);
  const [cancelError, setCancelError] = React.useState("");
  const [paying, setPaying] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const mounted = React.useRef(true);
  const requestId = React.useRef(0);
  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const muted = useColorModeValue("gray.600", "gray.400");
  const inset = useColorModeValue("gray.50", "whiteAlpha.50");

  const stripeCustomerId = user?.stripeCustomerId;
  const load = React.useCallback(async () => {
    const id = ++requestId.current;
    if (!stripeCustomerId) {
      setItems([]);
      setError("");
      setNotice("");
      setSelected(null);
      setRefreshing(false);
      onTypesChange?.([]);
      return false;
    }
    setRefreshing(true);
    try {
      const result = await getBillingAttention();
      if (mounted.current && id === requestId.current) {
        setItems(result);
        onTypesChange?.(result.map((item) => item.type));
        setError("");
        return true;
      }
      return false;
    } catch {
      if (mounted.current && id === requestId.current)
        setError("Billing details are unavailable. Please try again.");
      return false;
    } finally {
      if (mounted.current && id === requestId.current) setRefreshing(false);
    }
  }, [onTypesChange, stripeCustomerId]);
  const refresh = React.useCallback(async () => {
    const ok = await load();
    if (ok) {
      try {
        const fresh = await getSession();
        if (mounted.current) setUser(fresh);
        await onRefresh();
      } catch {
        if (mounted.current)
          setError(
            "Billing refreshed. Account details could not be refreshed. Please try again."
          );
      }
    }
  }, [load, setUser, onRefresh]);
  React.useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);
  React.useEffect(() => {
    const onFocus = () => {
      if (!selected && !canceling) void refresh();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onFocus();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [refresh, selected, canceling]);

  const cancel = async () => {
    if (!selected) return;
    setCanceling(true);
    setCancelError("");
    try {
      const result = await cancelBillingSubscription(selected);
      setNotice(
        result.reverted
          ? "Trial ended. Your previous plan is being restored."
          : result.scheduled
          ? "Cancellation scheduled. Your subscription will not renew."
          : "Subscription canceled. It will not renew."
      );
      setSelected(null);
      await refresh();
    } catch (e: any) {
      setCancelError(
        e.response?.data?.error ||
          "We couldn’t confirm cancellation. Refresh billing details before trying again."
      );
    } finally {
      setCanceling(false);
    }
  };
  const paymentDetails = async () => {
    setPortalLoading(true);
    try {
      window.location.assign(await createManageSubscriptionSession());
    } catch {
      setError("Payment details are unavailable. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const pay = async (
    item: BillingAttentionItem,
    invoice: BillingAttentionItem["invoices"][number]
  ) => {
    if (paying) return;
    setPaying(true);
    setError("");
    setNotice("");
    try {
      if (invoice.prepareToken) {
        const result = await prepareBillingInvoice(invoice.prepareToken);
        window.location.assign(result.url);
        return;
      }
      const url = invoice.url;
      // Re-run server eligibility immediately before leaving for payment.
      const fresh = await getBillingAttention();
      setItems(fresh);
      onTypesChange?.(fresh.map((row) => row.type));
      const current = fresh
        .filter((row) => row.type === item.type)
        .flatMap((row) => row.invoices)
        .find((candidate) =>
          invoice.paymentKey
            ? candidate.paymentKey === invoice.paymentKey
            : !!url && candidate.url === url
        );
      if (current?.url) {
        window.location.assign(current.url);
      } else {
        setNotice(
          "This invoice is no longer available for payment. Billing details have been refreshed."
        );
      }
    } catch (e: any) {
      setError(
        e.response?.data?.error ||
          "Billing details are unavailable. Please try again."
      );
    } finally {
      setPaying(false);
    }
  };
  const serviceDate = (value: string) =>
    new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });

  if (!user || !stripeCustomerId) return null;
  if (items?.length === 0 && !error && !notice) return null;
  return (
    <Box
      role={items?.length ? "region" : undefined}
      aria-label={items?.length ? "Billing Attention" : undefined}
    >
      {!!items?.length && (
        <Flex justify="space-between" align="center" mb={3} gap={3}>
          <Heading size="sm" fontWeight="600">
            Billing Attention
          </Heading>
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<RepeatIcon />}
            onClick={() => void refresh()}
            isLoading={refreshing}
            loadingText="Refreshing"
          >
            Refresh
          </Button>
        </Flex>
      )}
      <VStack align="stretch" spacing={3}>
        {notice && (
          <Box
            role="status"
            borderWidth="1px"
            borderColor={border}
            bg={bg}
            borderRadius="lg"
            p={4}
          >
            <Text fontSize="sm">{notice}</Text>
          </Box>
        )}
        {error && (
          <Flex
            role="alert"
            borderWidth="1px"
            borderColor={border}
            bg={bg}
            borderRadius="lg"
            p={4}
            gap={3}
            align="center"
            justify="space-between"
            flexWrap="wrap"
          >
            <Text fontSize="sm">{error}</Text>
            <Button
              size="sm"
              variant="outline"
              ml="auto"
              onClick={() => void refresh()}
            >
              Retry
            </Button>
          </Flex>
        )}
        {items === null && !error && (
          <Box
            aria-label="Loading billing details"
            bg={bg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="lg"
            p={6}
          >
            <Skeleton height="18px" width="160px" mb={4} />
            <Skeleton height="14px" width="70%" />
            <Skeleton height="32px" width="140px" mt={5} ml="auto" />
          </Box>
        )}
        {items?.map((item, index) => (
          <Box
            key={item.token}
            data-testid={`billing-item-${index}`}
            bg={bg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="xl"
            overflow="hidden"
          >
            <Box p={{ base: 4, md: 5 }}>
              <Flex
                justify="space-between"
                gap={3}
                align="start"
                flexWrap="wrap"
              >
                <Box>
                  <Heading size="sm">{productName(item)}</Heading>
                  <Text fontSize="sm" color={muted} mt={1}>
                    {planName(item)}
                  </Text>
                </Box>
                <Badge
                  colorScheme={item.status === "paused" ? "gray" : "orange"}
                  variant="subtle"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontWeight="500"
                  textTransform="none"
                >
                  {statusLabel(item)}
                </Badge>
              </Flex>
              <Text fontSize="sm" mt={4}>
                {item.status === "paused"
                  ? "Your subscription is paused."
                  : item.pendingUpdate
                  ? "Complete payment to finish your plan change."
                  : item.invoices.length
                  ? "An invoice needs your attention."
                  : "Review your payment details to continue this subscription."}
              </Text>
              <Text fontSize="sm" color={muted} mt={1}>
                Current access: {accessName(item, user)}
              </Text>
              {item.cancelAtPeriodEnd && (
                <Text fontSize="sm" color={muted} mt={2}>
                  Cancellation scheduled for {endDate(item)}.
                </Text>
              )}
              {item.billingLocked && (
                <Text fontSize="sm" color={muted} mt={2}>
                  Finish managing your trial before changing this subscription.
                </Text>
              )}
            </Box>
            {item.invoices.length > 0 && (
              <VStack
                align="stretch"
                spacing={0}
                bg={inset}
                borderTopWidth="1px"
                borderColor={border}
              >
                {item.invoices.map((invoice, i) => (
                  <Flex
                    key={`${invoice.number}-${i}`}
                    px={{ base: 4, md: 5 }}
                    py={3}
                    borderTopWidth={i ? "1px" : 0}
                    borderColor={border}
                    align="center"
                    justify="space-between"
                    gap={3}
                    flexWrap="wrap"
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="600">
                        {money(invoice.amount, invoice.currency)} due
                      </Text>
                      <Text fontSize="xs" color={muted} mt={0.5}>
                        {serviceDate(invoice.periodStart)} –{" "}
                        {serviceDate(invoice.periodEnd)}
                      </Text>
                      {invoice.number && (
                        <Text fontSize="xs" color={muted} mt={0.5}>
                          Invoice {invoice.number}
                        </Text>
                      )}
                    </Box>
                    {invoice.url || invoice.prepareToken ? (
                      <Button
                        onClick={() => void pay(item, invoice)}
                        isLoading={paying}
                        rightIcon={<ExternalLinkIcon />}
                        colorScheme="brand"
                        size="sm"
                        ml="auto"
                      >
                        Pay and restore
                      </Button>
                    ) : (
                      <Text fontSize="xs" color={muted}>
                        Payment link unavailable
                      </Text>
                    )}
                  </Flex>
                ))}
              </VStack>
            )}
            <Flex
              px={{ base: 4, md: 5 }}
              py={3}
              borderTopWidth="1px"
              borderColor={border}
              justify="flex-end"
              gap={2}
              flexWrap="wrap"
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={paymentDetails}
                isLoading={portalLoading}
              >
                Payment details
              </Button>
              {item.cancellationMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelected(item);
                    setCancelError("");
                  }}
                >
                  Cancel subscription
                </Button>
              )}
            </Flex>
          </Box>
        ))}
      </VStack>
      <Modal
        isOpen={!!selected}
        onClose={() => {
          if (!canceling) setSelected(null);
        }}
        isCentered
        closeOnOverlayClick={!canceling}
        closeOnEsc={!canceling}
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent mx={4} borderRadius="xl">
          <ModalHeader>
            Cancel {selected ? productName(selected) : "subscription"}?
          </ModalHeader>
          <ModalCloseButton isDisabled={canceling} />
          <ModalBody>
            <Text fontSize="sm">
              {selected?.cancellationMode === "period_end"
                ? `Your subscription will end on ${endDate(
                    selected
                  )} and will not renew.`
                : selected?.cancellationMode === "revert_trial"
                ? "Your trial will end now and your previous plan will be restored."
                : "Your subscription will end immediately and will not renew."}
            </Text>
            {cancelError && (
              <Text role="alert" color="red.400" fontSize="sm" mt={3}>
                {cancelError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={3} flexWrap="wrap">
            {cancelError && (
              <Button
                variant="outline"
                onClick={async () => {
                  setSelected(null);
                  await refresh();
                }}
              >
                Refresh billing
              </Button>
            )}
            <Button
              variant="ghost"
              isDisabled={canceling}
              onClick={() => setSelected(null)}
            >
              Keep subscription
            </Button>
            <Button colorScheme="red" isLoading={canceling} onClick={cancel}>
              Confirm cancellation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
